<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { RouteNames } from '../../router';
import { useUserStore } from '../../stores/user';

const { isAdmin } = useUserStore();
const route = useRoute();
const showMobileMenu = ref(false);

const isHomeActive = computed(() => {
  return route.path === '/';
});

function toggleMobileMenu() {
  showMobileMenu.value = !showMobileMenu.value;
}

function closeMobileMenu() {
  showMobileMenu.value = false;
}
</script>

<template>
  <div class="header-nav">
    <button class="mobile-menu" v-on:click="toggleMobileMenu()">
      {{ $t('header.nav.menu') }}
    </button>
    <ul class="header-nav-menu" :class="{ 'is-open': showMobileMenu }" data-cy="navigation-bar">
      <li class="header-nav-menu-item">
        <router-link
          to="/orders?autoScroll=true&show=orders"
          data-cy="orders"
          :class="isHomeActive ? 'router-link-active' : ''"
          v-on:click="closeMobileMenu">
          {{ $t('header.nav.orders') }}
        </router-link>
      </li>
      <li class="header-nav-menu-item">
        <router-link
          :to="{ name: RouteNames.ProductsAvailabilityDefault }"
          data-cy="availability"
          v-on:click="closeMobileMenu">
          {{ $t('header.nav.availability') }}
        </router-link>
      </li>
      <li class="header-nav-menu-item">
        <router-link to="/accounting" data-cy="accounting" v-on:click="closeMobileMenu">
          {{ $t('header.nav.accounting') }}
        </router-link>
      </li>
      <li class="header-nav-menu-item">
        <router-link to="/warranty-cases" data-cy="warranty-cases" v-on:click="closeMobileMenu">
          {{ $t('header.nav.warranty_cases') }}
        </router-link>
      </li>
      <li class="header-nav-menu-item">
        <router-link to="/return-cases" data-cy="return-cases" v-on:click="closeMobileMenu">
          {{ $t('header.nav.return_cases') }}
        </router-link>
      </li>
      <li class="header-nav-menu-item">
        <router-link to="/products" data-cy="products" v-on:click="closeMobileMenu">
          {{ $t('header.nav.products') }}
        </router-link>
      </li>
      <li class="header-nav-menu-item">
        <router-link to="/marketing" data-cy="marketing" v-on:click="closeMobileMenu">
          {{ $t('header.nav.marketing') }}
        </router-link>
      </li>
      <li class="header-nav-menu-item" data-cy="contact-us">
        <router-link to="/contact-us" v-on:click="closeMobileMenu">
          {{ $t('header.nav.contact') }}
        </router-link>
      </li>
      <li class="header-nav-menu-item">
        <router-link :to="{ name: RouteNames.Faqs }" data-cy="help-center" v-on:click="closeMobileMenu">
          {{ $t('header.nav.help_center') }}
        </router-link>
      </li>
      <li v-if="isAdmin()" class="header-nav-menu-item">
        <router-link to="/admin" data-cy="admin" v-on:click="closeMobileMenu">
          {{ $t('header.nav.admin') }}
        </router-link>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.header-nav {
  height: 100%;

  .header-nav-menu {
    display: flex;
    align-items: center;
    background-color: unset;
    height: 100%;
    margin: 0;
    padding: 0;
    list-style-type: none;

    &.is-open {
      @include breakpoint(medium) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        position: fixed;
        bottom: 1.25rem;
        right: 1.25rem;
        height: fit-content;
        width: 250px;
        z-index: 8;
        padding: 1.25rem 1.25rem;
        border-radius: 0.625rem;
        background-color: var(--primary-bg-color);
        top: 66px;
        left: 8px;
        border: 1px solid var(--primary-text-color);

        .header-nav-menu-item {
          padding: 0.3125rem;
          &.parent-action {
            margin: 0;
            ul.child-menu-actions {
              left: calc(-13.125rem + 100%);
            }
          }
        }
      }
    }

    .header-nav-menu-item {
      display: flex;
      align-items: center;
      padding: 0 0.625rem;
      margin: 0;
      width: fit-content;
      word-break: normal;

      @include breakpoint(untilxlarge) {
        padding: 0 8px 0 0;

        &:last-child {
          padding-right: 0;
        }
      }

      @include breakpoint(medium) {
        flex-direction: column;
        align-items: flex-start;
        padding: 0 2.5px;
      }

      > span,
      > a {
        font-weight: 500;
        font-size: 0.875rem;
        line-height: 150%;
        color: var(--primary-text-remark-unselected-color);
        transition: all 250ms;

        &.router-link-active {
          font-weight: 700;
          color: var(--primary-text-focus-color);
          text-decoration: underline;
        }
      }

      &:hover {
        > span,
        > a {
          color: var(--primary-text-focus-color);
        }
      }

      &.parent-action {
        position: relative;
        margin: auto;

        @media (hover) {
          &:hover {
            ul.child-menu-actions {
              display: block;
            }
          }
        }

        @media (hover: none) {
          &.hovered {
            ul.child-menu-actions {
              display: block;
            }
          }
        }

        ul.child-menu-actions {
          position: absolute;
          display: none;
          left: 0;
          top: 100%;
          padding: 0.625rem;
          background: var(--secondary-bg-color);
          list-style: none;
          z-index: 10;

          @include breakpoint(medium) {
            position: static;
            padding: 0 0 0 10px;
            background: none;
          }

          > li {
            margin: 0;
            padding: 0.3125rem;
            font-size: 0.875rem;
            width: 13.125rem;
            text-align: left;

            > a {
              color: var(--primary-text-disabled-color);

              @include breakpoint(medium) {
                color: var(--primary-text-remark-unselected-color);
              }

              &:hover {
                color: var(--secondary-text-color);

                @include breakpoint(medium) {
                  color: var(--primary-text-color);
                }
              }
            }
          }
        }
      }
    }

    @include breakpoint(medium) {
      display: none;
    }
  }

  .mobile-menu {
    display: none;
    font-size: 0.875rem;
    margin: 11px 10px;
    padding: 0.75rem 1rem;
    border-radius: 10rem;
    color: var(--secondary-text-color);
    background-color: var(--primary-button-bg-color);
    z-index: 9;
    border: none;
    border-color: 1px solid var(--border-color);

    &:before {
      content: '\2630';
      width: 0.9375rem;
      height: 0.9375rem;
      margin-right: 0.625rem;

      @include breakpoint(small) {
        margin-right: 0;
      }
    }

    @include breakpoint(medium) {
      display: block;
    }

    @include breakpoint(small) {
      font-size: 0.775rem;
      margin: 11px 0px;
      padding: 0.85rem 0.6rem;
    }
  }
}
</style>
